import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import { isGQLErrorOfType } from '@/utils/errors';
import { createSlugAvailabilityKey } from '@/utils/queryKeys';
import type {
  CheckSlugAvailabilityQuery,
  CheckSlugAvailabilityQueryVariables,
} from './useCheckSlugAvailability.generated';

const query = gql`
  query CheckSlugAvailability($input: CheckSlugAvailabilityInput!) {
    checkSlugAvailability(input: $input)
  }
`;

const selector = (data: CheckSlugAvailabilityQuery) => ({
  isAvailable: data.checkSlugAvailability ?? true,
  validationError: false,
});

export const useCheckSlugAvailability = (slug: string) => {
  const { data, error, isPending, isError, refetch } = useQuery({
    queryKey: createSlugAvailabilityKey(slug),
    queryFn: async () => {
      try {
        return await gqlClient.request<
          CheckSlugAvailabilityQuery,
          CheckSlugAvailabilityQueryVariables
        >(query, { input: { slug } });
      } catch (err) {
        // Suppress console errors when validation fails
        if (isGQLErrorOfType(err, 'MerValidationErrFailedValidation')) {
          return { checkSlugAvailability: false };
        }
        throw err;
      }
    },
    select: selector,
  });

  return {
    isSlugAvailable: data?.isAvailable ?? true,
    validationError: data?.validationError ?? false,
    error,
    refetch,
    isError,
    isPending,
  };
};
